import { lazy, ComponentType } from "react";

/**
 * Wraps React.lazy() with retry logic to handle chunk loading failures
 * that occur after a new deploy (old chunk files no longer exist on CDN).
 *
 * On failure: clears caches, unregisters service workers, and reloads once.
 * Uses sessionStorage to prevent infinite reload loops.
 */
export default function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>
) {
  return lazy(() => {
    const hasRefreshed = JSON.parse(
      window.sessionStorage.getItem("chunk-reload-retry") || "false"
    );

    return componentImport().catch((error: Error) => {
      if (!hasRefreshed) {
        window.sessionStorage.setItem("chunk-reload-retry", "true");

        // Clear service worker and caches before reload
        if ("caches" in window) {
          caches.keys().then((names) => {
            for (const name of names) caches.delete(name);
          });
        }
        if ("serviceWorker" in navigator) {
          navigator.serviceWorker.getRegistrations().then((registrations) => {
            for (const reg of registrations) reg.unregister();
          });
        }

        window.location.reload();
        // Return a never-resolving promise to prevent rendering while reload happens
        return new Promise(() => {});
      }

      // Already retried once — clear the flag and let the error propagate
      // so ErrorBoundary can show the refresh modal.
      window.sessionStorage.removeItem("chunk-reload-retry");
      throw error;
    });
  });
}
